import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheExpiryConstants, CacheKeyConstants, CacheService, DateFormatConstants, FileData, isNullOrUndefined } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import _moment from 'moment-timezone';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  Address,
  Driver,
  FavoriteDriver,
  FavoriteLocation,
  FileUpload,
  IgnoredDriver,
  Location,
  OrganizationRider,
  OrganizationRiderDetail,
  OrganizationRiderListItem,
  OrganizationRiderPool,
  OrganizationRiderServiceLevel,
  Payment,
  PaymentAccount,
  PaymentMethod,
  ReservationChargeDetail,
  RideLimitDetail,
  Rider,
  Transaction
} from '../../models';
import { RiderProfileDetail } from '../../models/rider-profile-detail';

const moment = _moment;

@Injectable({
  providedIn: 'root'
})
export class OrganizationRiderService extends UzurvRestService {
  readonly resourceUrl = 'organizationRiders/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    protected domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Searches for organization riders on a searchString
   *
   * @param organizationId
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission rider_search
   */
  searchRiders(searchString: string, pageNumber = 0, pageSize = 7): Observable<Array<OrganizationRiderListItem>> {
    return this.get<Array<OrganizationRiderListItem>>(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, OrganizationRiderListItem)));
  }

  /**
   * Finds and organization rider by organization ID and rider ID
   *
   * @param organizationId
   * @param riderId
   * @permission organization_rider_read
   */
  findOrganizationRider(organizationId: number, riderId: number): Observable<OrganizationRiderDetail> {
    return this.get<OrganizationRiderDetail>(`${this.resourceUrl}${riderId}`).pipe(
      map(response => Deserialize(response, OrganizationRiderDetail))
    );
  }

  /**
   * Returns a rider profile by organization rider ID
   *
   * @param organizationRiderId
   * @permission organization_rider_read
   *
   */
  getRiderProfileByOrganizationRiderId(organizationRiderId: number): Observable<RiderProfileDetail> {
    return this.get<RiderProfileDetail>(`${this.resourceUrl}${organizationRiderId}/riderProfile/`).pipe(
      map(response => Deserialize(response, RiderProfileDetail))
    );
  }

  /**
   * Creates a new organisation rider with the supplied object
   *
   * @param organizationRider
   * @permission organization_rider_create
   */
  createOrganizationRider(organizationRider: OrganizationRider): Observable<OrganizationRiderDetail> {
    return this.post<OrganizationRiderDetail>(this.resourceUrl, Serialize(organizationRider, OrganizationRider)).pipe(
      map(response => Deserialize(response, OrganizationRiderDetail))
    );
  }

  /**
   * Updates an existing organization rider with the supplied object
   *
   * @param organizationRiderDetail
   * @permission organization_rider_update
   */
  patchOrganizationRider(
    organizationRiderDetail: Partial<OrganizationRiderDetail>,
    organizationRiderPool?: OrganizationRiderPool
  ): Observable<OrganizationRiderDetail> {
    const rider = new Rider();
    Object.assign(rider, {
      firstName: organizationRiderDetail.firstName,
      lastName: organizationRiderDetail.lastName,
      phoneNumber: organizationRiderDetail.phoneNumber,
      smsEnabledPhone: organizationRiderDetail.smsEnabledPhone,
      emailAddress: organizationRiderDetail.emailAddress,
      dateOfBirth: organizationRiderDetail.dateOfBirth
    });
    const organizationRider = new OrganizationRider();
    Object.assign(organizationRider, {
      id: organizationRiderDetail.organizationRiderId,
      partnerIdentifier: organizationRiderDetail.partnerIdentifier,
      riderEmailDeclined: organizationRiderDetail.riderEmailDeclined,
      firstName: organizationRiderDetail.firstName,
      lastName: organizationRiderDetail.lastName,
      phoneNumber: organizationRiderDetail.phoneNumber,
      smsEnabledPhone: organizationRiderDetail.smsEnabledPhone,
      emailAddress: organizationRiderDetail.emailAddress,
      dateOfBirth: organizationRiderDetail.dateOfBirth,
      eligible: organizationRiderDetail.eligible,
      organizationRiderPool: organizationRiderPool,
      rider: rider
    });

    return this.patch<OrganizationRiderDetail>(
      `${this.resourceUrl}${organizationRiderDetail.organizationRiderId}`,
      Serialize(organizationRider, OrganizationRider)
    ).pipe(map(response => Deserialize(response, OrganizationRiderDetail)));
  }

  /**
   * Updates an existing organization rider's address
   *
   * @param address
   */
  updateOrganizationRiderAddress(address: Address): Observable<Address> {
    return this.post<Address>(`${this.resourceUrl}addresses`, Serialize(address, Address)).pipe(
      map(response => Deserialize(response, Address))
    );
  }

  /* Addresses */

  /**
   * Returns the address for a rider by ID
   *
   * @param organizationRiderId
   * @permission organization_rider_read
   */

  getOrganizationRiderAddress(organizationRiderId: number): Observable<Address> {
    return this.get<Address>(`${this.resourceUrl}persons/${organizationRiderId}/address`).pipe(
      map(response => Deserialize(response, Address))
    );
  }

  /* Payment Methods */

  /**
   * Returns the available payment methods, overriding locally cached values if passed true
   *
   * @param overrideCache
   */
  getPaymentMethods(overrideCache = false): Observable<Array<PaymentMethod>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.PAYMENT_METHODS);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), PaymentMethod));
    }

    return this.get('paymentAccounts/paymentMethods/', {
      pageNumber: 0,
      pageSize: 100
    }).pipe(
      map(response => {
        this.cacheService.cacheData(CacheKeyConstants.PAYMENT_METHODS, JSON.stringify(response), CacheExpiryConstants.DOMAIN_CACHE_EXPIRY);
        return Deserialize(response, PaymentMethod);
      })
    );
  }

  /**
   * @param organizationRiderId
   * @param pickupDate
   * @permission organization_rider_read
   */
  getRideLimits(organizationRiderId: number, pickupDate: Date): Observable<RideLimitDetail> {
    return this.get<RideLimitDetail>(`${this.resourceUrl}${organizationRiderId}/rideLimits/`, {
      pickupDate: moment(pickupDate).format(DateFormatConstants.ISO_LOCAL_DATE)
    });
  }

  /**
   * Returns a list of favorite locations by rider ID
   *
   * @param organizationRiderId
   * @permission organization_rider_read
   */
  getFavoriteLocationsByRiderId(organizationRiderId: number): Observable<Array<FavoriteLocation>> {
    return this.get<Array<FavoriteLocation>>(`${this.resourceUrl}${organizationRiderId}/favoriteLocations/`).pipe(
      map(response => Deserialize(response, FavoriteLocation))
    );
  }

  /**
   * Adds a location to a riders favorite location list with a label
   *
   * @param organizationRiderId
   * @param location
   * @param label
   * @permission organization_rider_u[date]
   */
  addFavoriteLocationForRiderWithLabel(organizationRiderId: number, location: Location, label: string): Observable<FavoriteLocation> {
    return this.post<FavoriteLocation>(
      `${this.resourceUrl}${organizationRiderId}/favoriteLocations/?label=${label}`,
      Serialize(location)
    ).pipe(map(response => Deserialize(response, FavoriteLocation)));
  }

  /**
   * @param organizationRiderId
   * @param locationId
   * @permission organization_rider_update
   */
  deleteFavoriteLocation(organizationRiderId: number, locationId: number) {
    return this.delete<boolean>(`${this.resourceUrl}${organizationRiderId}/favoriteLocations/${locationId}`);
  }

  /**
   * Returns a payment account by ID
   *
   * @param id
   * @param overrideCache
   */
  findPaymentMethodById(id: number, overrideCache = false): Observable<PaymentMethod> {
    return this.getPaymentMethods(overrideCache).pipe(map(response => response.find(paymentMethod => paymentMethod.id === id)));
  }

  /**
   * Returns a payment account by name
   *
   * @param name
   * @param overrideCache
   */
  findPaymentMethodByName(name: string, overrideCache = false): Observable<PaymentMethod> {
    return this.getPaymentMethods(overrideCache).pipe(map(response => response.find(paymentMethod => paymentMethod.name === name)));
  }

  /**
   * Returns a payment account by ID
   *
   * @param organizationRiderId
   * @param paymentAccountId
   * @permission organization_rider_read
   */
  findPaymentAccountById(organizationRiderId: number, paymentAccountId: number): Observable<PaymentAccount> {
    return this.get(`${this.resourceUrl}${organizationRiderId}/paymentAccounts/${paymentAccountId}`).pipe(
      map(response => Deserialize(response, PaymentAccount))
    );
  }

  /**
   * Returns a list of payment accounts by user ID
   *
   * @param organizationRiderId
   * @permission organization_rider_read
   */
  getPaymentAccountsByOrganizationRiderId(organizationRiderId: number): Observable<Array<PaymentAccount>> {
    return this.get(`${this.resourceUrl}${organizationRiderId}/paymentAccounts/`, {
      pageNumber: 0,
      pageSize: 100
    }).pipe(map(response => Deserialize(response, PaymentAccount)));
  }

  /**
   * Deletes a payment account from the system
   *
   * @param organizationRiderId
   * @param paymentAccountId
   * @permission organization_rider_update
   */
  deletePaymentAccount(organizationRiderId: number, paymentAccountId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${organizationRiderId}/paymentAccounts/${paymentAccountId}`);
  }

  /**
   * Creates a new payment account with the provided object. Copying the
   * billing information solves a quick with Authorize.net where the address
   * isn't immediately available in the creation RO
   *
   * @param riderId
   * @param paymentAccount
   * @permission organization_rider_update
   */
  createPaymentAccount(riderId: number, paymentAccount: PaymentAccount): Observable<PaymentAccount> {
    return this.post<PaymentAccount>(`${this.resourceUrl}${riderId}/paymentAccounts/`, Serialize(paymentAccount))
      .pipe(
        map(response => {
          let savedPaymentAccount: PaymentAccount;

          if (response) {
            savedPaymentAccount = Deserialize(response, PaymentAccount);
            savedPaymentAccount = this.copyBillingInformation(paymentAccount, savedPaymentAccount);
          }
          return Deserialize(savedPaymentAccount, PaymentAccount);
        })
      )
      .pipe(
        catchError(err => {
          const message = isNullOrUndefined(err.message) ? '' : err.message;

          // Map to translated errors
          if (message.includes('Preauth failed')) {
            err.message = 'Error: Preauth failed';
          }

          return throwError(err);
        })
      );
  }

  /**
   * Updates an existing payment account
   *
   * @param organizationRiderId
   * @param paymentAccount
   * @permission organization_rider_update
   */
  updatePaymentAccount(organizationRiderId: number, paymentAccount: PaymentAccount): Observable<PaymentAccount> {
    return this.put<PaymentAccount>(
      `${this.resourceUrl}${organizationRiderId}/paymentAccounts/${paymentAccount.id}`,
      Serialize(paymentAccount)
    ).pipe(
      map(response => {
        let savedPaymentAccount: PaymentAccount;

        if (response) {
          savedPaymentAccount = Deserialize(response, PaymentAccount);
          savedPaymentAccount = this.copyBillingInformation(paymentAccount, savedPaymentAccount);
        }
        return Deserialize(savedPaymentAccount, PaymentAccount);
      })
    );
  }

  /**
   * Returns a list of payments by account ID
   *
   * @param paymentAccountId
   * @param pageNumber
   * @param pageSize
   * @permission organization_rider_read
   */
  getPaymentsByPaymentAccountId(paymentAccountId: number, pageNumber: number, pageSize: number): Observable<Payment> {
    return this.get(`${this.resourceUrl}paymentAccounts/${paymentAccountId}/payments/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Payment)));
  }

  /**
   * Returns a list of charges by organizationrider ID
   *
   * @param organizationRiderId
   * @param unpaid
   * @permission organization_rider_read
   */
  listAllCharges(organizationRiderId: number, unpaid: boolean): Observable<Array<ReservationChargeDetail>> {
    return this.get(`${this.resourceUrl}${organizationRiderId}/charges/`, {
      unpaidOnly: unpaid,
      pageSize: 1000,
      pageNumber: 0
    }).pipe(map(response => Deserialize(response, ReservationChargeDetail)));
  }
  /**
   * Collects payment for an organization rider
   *
   * @param organizationRiderId
   * @param paymentAccountId
   * @param chargeIds
   * @permission payment_processing
   */
  collectPaymentForCharges(organizationRiderId: number, paymentAccountId: number, chargeIds: Array<number>): Observable<Payment> {
    return this.post<Payment>(`${this.resourceUrl}${organizationRiderId}/charges/collect`, {
      organizationRiderId: organizationRiderId,
      paymentAccountId: paymentAccountId,
      chargeIds: chargeIds
    }).pipe(map(response => Deserialize(response, Payment)));
  }

  /**
   * Adds a driver to a riders favorite driver list
   *
   * @param organizationRiderId
   * @param driverId
   * @permission organization_rider_update
   */
  addFavoriteDriverToRiderWithId(organizationRiderId: number, driverId: number): Observable<FavoriteDriver> {
    return this.post<FavoriteDriver>(`${this.resourceUrl}${organizationRiderId}/favoriteDrivers/${driverId}`, null).pipe(
      map(response => Deserialize(response, FavoriteDriver))
    );
  }

  /**
   * Removes a favorite driver from a riders favorite driver list by ID
   *
   * @param riderId
   * @param driverId
   * @permission organization_rider_update
   */
  removeFavoriteDriverFromRiderWithId(riderId: number, driverId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${riderId}/favoriteDrivers/${driverId}`);
  }

  /**
   * Tests if a rider has a favorite driver, and returns the driver if it exists
   *
   * @param organizationRider
   * @param driver
   */
  doesRiderHaveFavoriteDriver(organizationRider: OrganizationRider, driver: Driver): boolean {
    if (isNullOrUndefined(organizationRider.rider.favoriteDrivers)) {
      return false;
    }

    if (isNullOrUndefined(driver)) {
      return false;
    }

    return !isNullOrUndefined(organizationRider.rider.favoriteDrivers.find(d => d.id === driver.id));
  }

  /**
   *
   * @param organizationRiderId
   * @permission organization_rider_read
   */
  listIgnoredDriversByOrganizationRiderId(organizationRiderId: number): Observable<Array<Driver>> {
    return this.get<Array<Driver>>(`${this.resourceUrl}${organizationRiderId}/ignoredDrivers/`).pipe(
      map(response => Deserialize(response, Driver))
    );
  }

  /**
   * Adds a driver to a riders ignored drivers list
   *
   * @param organizationRiderId
   * @param driverId
   * @permission organization_rider_update
   */
  addIgnoredDriverToRider(organizationRiderId: number, driverId: number): Observable<IgnoredDriver> {
    return this.post<IgnoredDriver>(`${this.resourceUrl}${organizationRiderId}/ignoredDrivers/${driverId}`, null).pipe(
      map(response => Deserialize(response, IgnoredDriver))
    );
  }

  /**
   * Removes a driver from a riders ignored drivers list
   *
   * @param organizationRiderId
   * @param driverId
   * @permission organization_rider_update
   */
  removeIgnoredDriverFromRider(organizaitonRiderId: number, driverId): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${organizaitonRiderId}/ignoredDrivers/${driverId}`);
  }

  /**
   * Tests whether a rider has ignored a driver and returns the driver if so
   *
   * @param organizationRider
   * @param driver
   */
  doesRiderHaveIgnoredDriver(organizationRider: OrganizationRider, driver: Driver): boolean {
    if (isNullOrUndefined(organizationRider.rider.ignoredDrivers)) {
      return false;
    }

    return !isNullOrUndefined(organizationRider.rider.ignoredDrivers.find(d => d.id === driver.id));
  }

  /**
   * @param file
   * @permission rider_eligibility_file_upload
   */
  uploadRiderEligibilityCSV(file: File) {
    return this.uploadWithProgress(`${this.resourceUrl}fileUpload/`, file);
  }

  /**
   * @permission rider_eligibility_file_upload
   */
  getRiderEligibilityUploadStatuses(pageNumber = 0, pageSize = 1000): Observable<Array<FileUpload>> {
    return this.get<Array<FileUpload>>(`${this.resourceUrl}fileUploads`, { pageNumber: pageNumber, pageSize: pageSize }).pipe(
      map(response => Deserialize(response, FileUpload))
    );
  }

  /**
   * @permission rider_eligibility_file_upload
   */
  getRiderEligibilityUploadErrorFile(fileId: number): Observable<FileData> {
    return this.get<FileData>(`${this.resourceUrl}fileUploads/${fileId}/errorCsv`).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * list all payments for an organization rider
   *
   * @param organizationRiderId
   * @param pageNumber
   * @param pageSize
   * @permission payment_processing
   */
  listPaymentsByOrganizationRiderId(organizationRiderId: number, pageNumber: number, pageSize: number): Observable<Array<Payment>> {
    return this.get<Array<Payment>>(`${this.resourceUrl}${organizationRiderId}/payments/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Payment)));
  }

  /**
   * list all transactions for a payment
   *
   * @param organizationRiderId
   * @param paymentId
   * @permission payment_processing
   */
  listTransactionsByOrganizationRiderIdAndPaymentId(organizationRiderId: number, paymentId: number): Observable<Array<Transaction>> {
    return this.get<Array<Transaction>>(`${this.resourceUrl}${organizationRiderId}/payments/${paymentId}/transactions/`, {
      pageNumber: 0,
      pageSize: 1000
    }).pipe(map(response => Deserialize(response, Transaction)));
  }

  /**
   * creates a new payment
   *
   * @param organizationRiderId
   * @param payment
   * @permission payment_processing
   */
  createPayment(organizationRiderId: number, payment: Payment): Observable<Payment> {
    return this.post<Payment>(`${this.resourceUrl}${organizationRiderId}/payments/`, payment).pipe(
      map(response => Deserialize(response, Payment))
    );
  }

  /**
   * updates a payment
   *
   * @param organizationRiderId
   * @param payment
   * @permission payment_processing
   */
  updateProcessedPayment(organizationRiderId: number, payment: Payment): Observable<Payment> {
    return this.put<Payment>(`${this.resourceUrl}${organizationRiderId}/payments/${payment.id}`, payment).pipe(
      map(response => Deserialize(response, Payment))
    );
  }

  /**
   * fetch a transaction
   *
   * @param organizationRiderId
   * @param paymentId
   * @param transactionId
   * @permission payment_processing
   */
  findTransactionForOrganizationRider(organizationRiderId: number, paymentId: number, transactionId: number): Observable<Transaction> {
    return this.get<Transaction>(`${this.resourceUrl}${organizationRiderId}/payments/${paymentId}/transactions/${transactionId}`).pipe(
      map(response => Deserialize(response, Transaction))
    );
  }

  /* Service Levels */

  /**
   * Gets all OrganizationRiderServiceLevels for an OrganizationRider
   *
   * @param organizationRiderId
   */
  listOrganizationRiderServiceLevels(organizationRiderId: number): Observable<Array<OrganizationRiderServiceLevel>> {
    return this.get<OrganizationRiderServiceLevel>(`${this.resourceUrl}${organizationRiderId}/serviceLevels/`).pipe(
      map(response => Deserialize(response, OrganizationRiderServiceLevel))
    );
  }

  /**
   *
   * @param organizationRiderId
   */
  sendUzurvRideAppInvitationToOrganizationRider(organizationRiderId: number): Observable<boolean> {
    return this.post<boolean>(`${this.resourceUrl}${organizationRiderId}/uzurvRideInvitation`, null);
  }

  /**
   * Adds a OrganizationRiderServiceLevel to an OrganizationRider
   *
   * @param organizationRiderId
   * @param organizationRiderServiceLevel
   */
  addOrganizationRiderServiceLevel(
    organizationRiderId: number,
    organizationRiderServiceLevel: OrganizationRiderServiceLevel
  ): Observable<OrganizationRiderServiceLevel> {
    return this.post<OrganizationRiderServiceLevel>(
      `${this.resourceUrl}${organizationRiderId}/serviceLevels/`,
      Serialize(organizationRiderServiceLevel)
    ).pipe(map(response => Deserialize(response, OrganizationRiderServiceLevel)));
  }

  /**
   * @param organizationRiderId
   * @param organizationRiderServiceLevelId
   * @permission organization_rider_update
   */
  disableOrganizationRiderServiceLeve(organizationRiderId: number, organizationRiderServiceLevelId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${organizationRiderId}/serviceLevels/${organizationRiderServiceLevelId}`);
  }

  /**
   * Copies values from one payment account to another
   *
   * @param from
   * @param to
   */
  private copyBillingInformation(from: PaymentAccount, to: PaymentAccount): PaymentAccount {
    if (from.billingStreetAddress1) {
      to.billingStreetAddress1 = from.billingStreetAddress1;
    }

    if (from.billingStreetAddress2) {
      to.billingStreetAddress2 = from.billingStreetAddress2;
    }

    if (from.billingState) {
      to.billingState = from.billingState;
    }

    if (from.billingCity) {
      to.billingCity = from.billingCity;
    }

    if (from.billingZipCode) {
      to.billingZipCode = from.billingZipCode;
    }

    return to;
  }
}
